/**
 * Absence: requests, types, the vacation deduction and limits, the vacation year.
 */
import { del, get, post, put } from '@/api/client';
import { useCan } from '@/auth/caps';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiUser } from './people';

export interface ApiAbsence {
  id: number;
  user_id: number;
  start_date: string;
  end_date: string;
  reason: string | null;
  status: 'pending' | 'accepted' | 'declined';
  /** Sykmeldingsgrad in percent (ftrl §8-13); null is a whole sick leave */
  sick_grade?: number | null;
  /** when the request was sent; the list route returns it (checked 2026-09-21) */
  created_at?: string;
  /** the last write to the row: a decision or an edit; an employee's cancel request does not touch it */
  updated_at?: string;
  absence_type: {
    id: number;
    name: string;
    color_code: string | null;
    is_sick_day?: boolean;
    is_deductible?: boolean;
    /** paid absence: a day of it with approved work is paid as both (D19 A, #27) */
    is_paid?: boolean;
    /** false: deleted since; a pending request on it is not approved as it is (2026-10-16) */
    is_active?: boolean;
    type?: string;
    /** web/src/pages/fravaer/category.ts */
    category?: string;
  } | null;
  user: Pick<ApiUser, 'id' | 'name' | 'lastname'> | null;
  /** what approving it took from the vacation balance, all of it and the part in next year's; null until charged */
  charged_days?: number | null;
  charged_days_next_year?: number | null;
}
/**
 * Every absence in the company, which the server answers only to a role with absence.view.
 * `enabled` so a screen that shows absences beside something else does not ask for them on
 * behalf of somebody who may not have them, and get a 403 in the console for it
 * (custom roles, 2026-09-16).
 */
/**
 * Everything a change to absence makes stale, decided here once (issue #76): the company's list,
 * the limits and the deduction previews, the people and the person (their balances), and the
 * pending requests. The union of what the scattered sites invalidated before.
 */
export function invalidateAbsences(qc: QueryClient) {
  return Promise.all(
    [
      ['absences'],
      ['absence-limits'],
      ['absence-standing'],
      ['deduction'],
      ['users'],
      ['user'],
      ['pending'],
      ['ferieliste'],
    ].map((queryKey) => qc.invalidateQueries({ queryKey })),
  );
}

export function useAbsences() {
  const can = useCan();
  return useQuery({
    queryKey: ['absences'],
    queryFn: () => get<ApiAbsence[]>('/admin/absence-requests'),
    enabled: can('fravaer.se'),
    staleTime: 30_000,
  });
}
export interface ApiAbsenceType {
  id: number;
  name: string;
  type: 'date' | 'time' | 'datetime' | string;
  color_code: string | null;
  is_deductible: boolean;
  is_paid: boolean;
  is_sick_day: boolean;
  is_active: boolean;
  max_times: number | null;
  max_days_per_use: number | null;
  limit_period: 'calendar_year' | 'rolling_year' | string | null;
  employer_paid_days: number | null;
  salary_type_code: number | string | null;
  absence_rate: number | string | null;
  /** the default catalogue (backend/services/absenceCatalogue.js); null on a company's own type */
  key: string | null;
  /** ferie, egenmelding, sykemelding, syk_barn, permisjon, annet (web/src/pages/fravaer/category.ts) */
  category?: string;
  is_builtin: boolean;
  day_basis: 'virkedager' | 'kalenderdager' | 'arbeidsdager' | null;
  payer: 'holiday_pay' | 'employer' | 'nav' | 'employer_then_nav' | 'unpaid' | null;
  max_days_per_period: number | null;
  /** the company's own request deadline in days */
  notice_days: number | null;
  /** the a-melding permisjon description, when the type is reported there and not as a lønnsart */
  a_melding: string | null;
}
/**
 * The company's absence types, in the order it made them. The route has no ORDER BY
 * (admin/absenceTypeControlller.js:57-63), so the row order Postgres happens to give is
 * not an order at all; sorting by id here is the one stored notion of the company's own
 * sequence, and every screen then shows the same one.
 *
 * `enabled` for a caller that offers them beside something else: the route wants
 * absence.view, and a role without it should not draw a 403 for a list it never shows.
 */
export function useAbsenceTypes(enabled = true) {
  // the route needs absence.view; an employee given "Se vaktplanen" opened Vaktplan and got two
  // 403s from the absence drawer asking for types it may not read (settings test, 2026-09-21)
  const can = useCan();
  return useQuery({
    queryKey: ['absence-types'],
    queryFn: async () => (await get<ApiAbsenceType[]>('/admin/absence-types')).sort((a, b) => a.id - b.id),
    enabled: enabled && can('fravaer.se'),
    staleTime: 5 * 60_000,
  });
}
export interface AbsenceWrite {
  user_id: number;
  absence_type_id: number;
  start_date: string;
  end_date: string;
  reason?: string | null;
  status?: 'pending' | 'accepted' | 'declined';
  /** Sykmeldingsgrad in percent, 20 to 100, or null for a whole sick leave (ftrl §8-13) */
  sick_grade?: number | null;
  /** why a manager changed it, kept in the absence's history (PUT only, #276) */
  change_reason?: string;
}

/** GET /absence-requests/deduction: the amount the existing calculation takes from the vacation balance on approval, for the preview before sending and before approving (TF-PRESERVATION-FINAL). */
export interface ApiDeduction {
  days: number;
  deductible: boolean;
  timed: boolean;
  /** the company's vacation count: virkedager or arbeidsdager */
  basis?: string;
  /** true when `days` is what the absence was charged, stored on it, not a count */
  charged?: boolean;
}
type DeductionAsk = {
  absence_type_id: number | null;
  start_date?: string;
  end_date?: string;
  request_id?: number | null;
} | null;
/** The deduction query, shared by the hook and the queue's per-row reads (#276) so both hit one cache entry. */
export const deductionQuery = (p: DeductionAsk) => ({
  queryKey: [
    'deduction',
    p?.absence_type_id ?? 0,
    p?.start_date ?? '',
    p?.end_date ?? '',
    p?.request_id ?? 0,
  ],
  queryFn: () =>
    get<ApiDeduction>(
      `/absence-requests/deduction?absence_type_id=${p?.absence_type_id}&start_date=${encodeURIComponent(p?.start_date ?? '')}&end_date=${encodeURIComponent(p?.end_date ?? '')}${p?.request_id ? `&request_id=${p.request_id}` : ''}`,
    ),
  enabled: !!p && !!p.absence_type_id && !!p.start_date && !!p.end_date,
  staleTime: 0,
  retry: false,
});
export function useDeduction(p: DeductionAsk) {
  return useQuery(deductionQuery(p));
}

export interface ApiAbsenceLimits {
  days: number;
  unit: string;
  warnings: { code: string; text: string }[];
}
/**
 * The limits an absence would pass, as warnings for the manager: GET /admin/absence-limits.
 * Never a refusal; the server registers and approves regardless (owner, 2026-09-18).
 */
export function useAbsenceLimits(
  p: {
    user_id: number;
    absence_type_id: number;
    start_date?: string | undefined;
    end_date?: string | undefined;
    exclude_id?: number | null | undefined;
  } | null,
) {
  const ready = !!p && !!p.user_id && !!p.absence_type_id && !!p.start_date && !!p.end_date;
  const q = new URLSearchParams();
  if (p && ready) {
    q.set('user_id', String(p.user_id));
    q.set('absence_type_id', String(p.absence_type_id));
    q.set('start_date', p.start_date as string);
    q.set('end_date', p.end_date as string);
    if (p.exclude_id) q.set('exclude_id', String(p.exclude_id));
  }
  return useQuery({
    queryKey: ['absence-limits', q.toString()],
    queryFn: () => get<ApiAbsenceLimits>(`/admin/absence-limits?${q.toString()}`),
    enabled: ready,
    staleTime: 0,
    retry: false,
  });
}

/** Where a person stands on vacation in a holiday year: GET /admin/absence-standing (requests.decide). */
export interface VacationStanding {
  year: number;
  /** what is left now; an approved absence is already taken off */
  left: number;
  /** taken: approved absences that have ended */
  used: number;
  /** approved and not ended yet */
  approved: number;
  /** left + used + approved: the year's days, carried-over ones included */
  limit: number;
  entitlement: number | null;
  carried: number;
}

/** The standing query, shared by the hook and the queue's per-row reads (#276). */
export const standingQuery = (userId: number | null | undefined, year: number | null, enabled: boolean) => ({
  queryKey: ['absence-standing', userId, year],
  queryFn: () =>
    get<{ vacation: VacationStanding }>(`/admin/absence-standing?user_id=${userId}&year=${year}`),
  enabled: enabled && !!userId && !!year,
  staleTime: 30_000,
  retry: false,
});
export function useAbsenceStanding(userId: number | null | undefined, year: number | null, enabled = true) {
  const can = useCan();
  return useQuery(standingQuery(userId, year, enabled && can('fravaer.godkjenne')));
}

/** "Nytt ferieår" (D32 B): per person, what is left, what is carried, and next year's entitlement. */
export interface VacationYearRow {
  user_id: number;
  name: string;
  left: number;
  next_year_balance: number;
  entitlement: number;
  carry: number;
  this_year: number;
  done: boolean;
}
export interface VacationYearPlan {
  year: number;
  can_apply: boolean;
  rows: VacationYearRow[];
  moved?: number;
}
export function useVacationYear(year: number, enabled: boolean) {
  return useQuery({
    queryKey: ['vacation-year', year],
    queryFn: () => get<VacationYearPlan>(`/admin/vacation-year?year=${year}`),
    enabled,
  });
}
/** GET /admin/ferieliste (#114): per person the year's ferie, the main period against § 7 (1), the notice under § 6 (2). */
export interface FerielisteAbsence {
  id: number;
  from: string;
  to: string;
  status: 'accepted' | 'pending';
  days: number;
  main_days: number;
  /** the Oslo date it was approved or got its present start; null while it is a request */
  fixed: string | null;
  notice_days: number | null;
  short_notice: boolean | null;
}
export interface FerielisteRow {
  user_id: number;
  name: string;
  approved_days: number;
  pending_days: number;
  main_days: number;
  main_pending_days: number;
  /** 18 virkedager, 15 where the company counts arbeidsdager, 0 for somebody starting after 15 August */
  main_required: number;
  main_missing: number;
  warning: 'declined' | 'not_planned' | null;
  short_notice: number;
  absences: FerielisteAbsence[];
}
export function useFerieliste(year: number) {
  return useQuery({
    queryKey: ['ferieliste', year],
    queryFn: () =>
      get<{ year: number; basis: string; rows: FerielisteRow[] }>(`/admin/ferieliste?year=${year}`),
  });
}
export function useVacationYearApply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { year: number; carry: Record<number, number> }) =>
      post<VacationYearPlan>('/admin/vacation-year', b),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['vacation-year'] }),
        qc.invalidateQueries({ queryKey: ['users'] }),
      ]),
  });
}

export function useAbsenceWrites() {
  const qc = useQueryClient();
  const done = () => invalidateAbsences(qc);
  return {
    approve: useMutation({
      // expected_days is the amount the manager saw; the server answers 409 with the actual count if it differs
      // change_reason goes into the history (#286, Saker asks for it); blank is left out
      mutationFn: (
        arg: number | { id: number; expected_days?: number | undefined; change_reason?: string | undefined },
      ) =>
        typeof arg === 'number'
          ? put<unknown>(`/admin/absence-requests/${arg}/approve`, {})
          : put<unknown>(`/admin/absence-requests/${arg.id}/approve`, {
              ...(arg.expected_days === undefined ? {} : { expected_days: arg.expected_days }),
              ...(arg.change_reason?.trim() ? { change_reason: arg.change_reason.trim() } : {}),
            }),
      onSettled: done,
    }),
    decline: useMutation({
      // expected_status is what the manager's screen showed; the server answers 409 when a colleague
      // has decided since, instead of undoing their decision (XU3, owner 2026-09-22)
      // change_reason goes into the history and the employee's message (NT20); blank is left out
      mutationFn: ({
        id,
        expected_status,
        change_reason,
      }: { id: number; expected_status: 'pending' | 'accepted'; change_reason?: string | undefined }) =>
        post<unknown>(`/admin/absence-requests/${id}/reject`, {
          expected_status,
          ...(change_reason?.trim() ? { change_reason: change_reason.trim() } : {}),
        }),
      onSettled: done,
    }),
    create: useMutation({
      mutationFn: (b: AbsenceWrite) => post<ApiAbsence>('/admin/absence-requests', b),
      onSettled: done,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: number; body: Partial<AbsenceWrite> }) =>
        put<ApiAbsence>(`/admin/absence-requests/${id}`, body),
      onSettled: done,
    }),
    remove: useMutation({
      mutationFn: (id: number) => del<unknown>(`/admin/absence-requests/${id}`),
      onSettled: done,
    }),
  };
}
export function useAbsenceTypeWrites() {
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: ['absence-types'] });
  return {
    create: useMutation({
      mutationFn: (b: Partial<ApiAbsenceType>) => post<ApiAbsenceType>('/admin/absence-types', b),
      onSettled: done,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: number; body: Partial<ApiAbsenceType> }) =>
        put<ApiAbsenceType>(`/admin/absence-types/${id}`, body),
      onSettled: done,
    }),
  };
}
export function useMyAbsences(start: string, end: string) {
  return useQuery({
    queryKey: ['my-absences', start, end],
    queryFn: () => get<ApiAbsence[]>(`/absence-requests/date-range?start_date=${start}&end_date=${end}`),
    staleTime: 30_000,
  });
}
export function useMyAbsenceTypes() {
  return useQuery({
    queryKey: ['my-absence-types'],
    queryFn: () => get<ApiAbsenceType[]>('/absence-types'),
    staleTime: 5 * 60_000,
  });
}
